import { Box, CircularProgress } from "@mui/material";

interface LoadingProps {
  /**
   * Accessible label announced to screen readers while loading.
   * Defaults to a generic message; pass something specific
   * (e.g. "Loading appointments") when the context is known.
   */
  label?: string;
}

/**
 * App loading indicator: a centered spinner that fills whatever container it is in.
 *
 * **It used to be a full-screen `<Backdrop>`** (FE-2), and every one of its ~30 call sites either
 * replaces the content below it or fills one card — none of them wanted the app dimmed. The worst
 * of it is `CaregiverPatientView`, where four independent cards each render this while their own
 * fetch is in flight: four dark overlays over the whole application, for four spinners that belong
 * inside four cards.
 *
 * The `inline` prop that used to select this behaviour is **gone rather than re-defaulted**. An
 * opt-in that every caller should set is a trap with documentation attached — it leaves the wrong
 * thing one forgotten prop away, which is how the four backdrops got there in the first place.
 *
 * **If something genuinely needs to block the app, it is not this component.** `LoadingBackdrop`
 * (driven by `useLoadingBackdrop`, mounted once in `index.tsx`) is the app-wide overlay, so the
 * decision to dim everything is made in one place instead of by a default.
 *
 * Note: this intentionally does NOT auto-dismiss on a timer. A previous version force-cleared
 * itself after 30s and raised a global "Something went wrong" error even while the request was
 * still in flight, surfacing phantom errors on slow networks. Callers that can genuinely fail
 * should render <LoadError /> on a real error instead of relying on a timeout.
 */
const Loading = ({ label = "Loading, please wait" }: LoadingProps) => (
  <Box
    role="status"
    aria-live="polite"
    aria-label={label}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      // Enough height to read as a loading region on its own, and it grows into the space when the
      // caller gives it one: a page replacement gets the content area, a card gets the card.
      flex: 1,
      minHeight: 160,
      p: 4,
    }}
  >
    <CircularProgress aria-hidden />
  </Box>
);

export default Loading;
